/*import Axios, { AxiosInstance } from "axios";
import AuthService from "@/core/services/AuthService";

import router from "@/router";
import { App } from "vue";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";*/

import axios, { AxiosInstance } from "axios";
import { getActivePinia } from "pinia";
import { useToast } from "vue-toastification";
import { App } from "vue";
import VueAxios from "vue-axios";
import HTTPService from "./HTTPService";
import UserService from "@/core/services/UserService";
import router from "@/router";
import { Pages } from "@/store/enums/StoreEnums";
import { useBanStore } from "@/store/pinia/BanModule";
import ConfigModule from "@/store/modules/ConfigModule";
import { createStore } from "vuex";
import RequestService from "@/core/services/RequestService";

const store = createStore({ modules: { ConfigModule } });

const toast = useToast();

/** Текст из тела ApiResponse (4xx: messages.form[], 5xx: messages[]). */
function messageFromApiPayload(data: unknown): string {
  if (!data || typeof data !== "object") {
    return "Ошибка запроса";
  }
  const d = data as Record<string, unknown>;
  const messages = d.messages;
  if (Array.isArray(messages) && messages.length > 0) {
    const first = messages[0];
    if (typeof first === "string") {
      return first;
    }
  }
  if (messages && typeof messages === "object" && !Array.isArray(messages)) {
    const form = (messages as { form?: unknown }).form;
    if (Array.isArray(form) && form.length > 0 && typeof form[0] === "string") {
      return form[0];
    }
  }
  if (typeof d.message === "string") {
    return d.message;
  }
  return "Ошибка запроса";
}

class ApiService {
  public static config = {
    baseURL: process.env.VUE_APP_API_URL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      // Accept: "text/html",
      "X-Requested-With": "XMLHttpRequest",
      Authorization: "",
    },
  };

  public static vueInstance: App;

  public static async init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);

    ApiService.createRequestHandler();
    ApiService.events();

    await HTTPService.get("sanctum/csrf-cookie").then(async (res) => {
      if (typeof res !== "undefined") {
        store.commit("setStatusServer", true);
        await RequestService.me();
      } else store.commit("setStatusServer", false);
    });
  }

  private static _httpRequest: AxiosInstance = axios.create(
    ApiService.getConfig()
  );

  public static get httpRequest(): AxiosInstance {
    return ApiService._httpRequest;
  }

  public static set httpRequest(value: AxiosInstance) {
    ApiService._httpRequest = value;
  }

  public static events() {
    const currentRoute = router.currentRoute.value;

    ApiService._httpRequest.interceptors.response.use(
      (res) => {
        return res;
      },
      (err) => {
        if (typeof err.response === "undefined") {
          /*
          toast.error("Ошибка работы сервера", {
            timeout: 5000,
          });
          */
        } else {
          // Если вернуть err, то сообщение сразу перебросится в then
          switch (err.response.status) {
            case 401:
              if (
                currentRoute.name === Pages.LOGIN ||
                currentRoute.name === Pages.REGISTER
              ) {
                err.data = {
                  type: "error",
                  status: err.response.status,
                  need_redirect: false,
                  message: "Неправильный логин или пароль",
                };
                return err;
              } else {
                UserService.logout();
                if (router.currentRoute.value.name !== Pages.LOGIN) {
                  router.push({ name: Pages.LOGIN }).then(() => {
                    toast.error("Ошибка сессии", {
                      timeout: 2000,
                    });
                  });
                }

                return {
                  data: {
                    type: "error",
                    message: "Неправильный логин или пароль",
                  },
                };
              }

            case 403: {
              const data = err.response?.data;
              if (data?.banned === true && data?.ban) {
                const pinia = getActivePinia();
                if (pinia) {
                  useBanStore(pinia).setBan({
                    message: data.message,
                    banned: true,
                    ban: data.ban,
                  });
                }
                router.push({ name: Pages.BANNED });
                return {
                  data: {
                    type: "error",
                    banned: true,
                    ban: data.ban,
                  },
                };
              }
              break;
            }

            case 404:
              err.data = {
                type: "error",
                status: err.response.status,
                need_redirect: true,
                message: "Ошибка. Страница не найдена",
              };
              return err;

            case 422:
              toast.error(messageFromApiPayload(err.response.data), {
                timeout: 2000,
              });

              err.data = {
                type: "error",
                status: err.response.status,
                need_redirect: false,
                message: messageFromApiPayload(err.response.data),
              };
              return err;

            case 419:
            case 500:
            case 502:
            case 503:
            case 504: {
              const msg = messageFromApiPayload(err.response?.data);
              toast.error(msg, { timeout: 4000 });
              return {
                data: {
                  type: "error",
                  status: err.response.status,
                  message: msg,
                },
                status: err.response.status,
                headers: err.response.headers,
              };
            }

            default: {
              const status = err.response?.status ?? 0;
              if (status >= 500 && status < 600) {
                const msg = messageFromApiPayload(err.response?.data);
                toast.error(msg, { timeout: 4000 });
                return {
                  data: {
                    type: "error",
                    status,
                    message: msg,
                  },
                  status,
                  headers: err.response.headers,
                };
              }

              console.error("Ошибка сервера:", err.response);

              router.push("/500").then(() => {
                toast.error("Ошибка в работе сервера", {
                  timeout: 2000,
                });
              });
              return Promise.reject(err);
            }
          }
        }

        return Promise.reject(err);
      }
    );
  }

  public static createRequestHandler() {
    ApiService._httpRequest = axios.create(ApiService.getConfig());
  }

  private static getConfig() {
    if (UserService.hasToken()) {
      ApiService.config.headers.Authorization = `Bearer ${UserService.getToken()}`;
    }
    return ApiService.config;
  }
}

export default ApiService;
